// ---------------------------------------------------------------------------
// ConversationListView.tsx
//
// Lists all conversations belonging to the authenticated user, loaded from
// Firestore's `conversations/` collection and grouped by date bucket.
//
// Entry points:
//   - Small icon button next to AISearchField in DiscoverView
//   - "Chats" menu item in the UserMenu in the AppBar
//
// Route: /chats
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    IconButton,
    Skeleton,
    Divider,
    Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import AddIcon from '@mui/icons-material/Add';
import {
    getFirestore,
    collection,
    query,
    where,
    orderBy,
    onSnapshot,
    Timestamp,
} from 'firebase/firestore';
import { useGetIdentity } from 'react-admin';
import { hoverScrollbar } from '../../../styles/hoverScrollbar';
import { FULL_SCREEN_INSETS } from '../../layout/chrome';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ConversationSummary {
    id: string;
    title: string;
    updatedAt: string; // ISO
    createdAt: string; // ISO
}

type DateGroup = 'Today' | 'Yesterday' | 'This week' | 'This month' | 'Older';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toIso(val: Timestamp | string | undefined | null): string {
    if (!val) return new Date().toISOString();
    if (val instanceof Timestamp) return val.toDate().toISOString();
    return val;
}

function getDateGroup(iso: string): DateGroup {
    const now = new Date();
    const date = new Date(iso);
    const diffMs = now.getTime() - date.getTime();
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    const isToday =
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();

    const yesterday = new Date(now);
    yesterday.setDate(now.getDate() - 1);
    const isYesterday =
        date.getDate() === yesterday.getDate() &&
        date.getMonth() === yesterday.getMonth() &&
        date.getFullYear() === yesterday.getFullYear();

    if (isToday) return 'Today';
    if (isYesterday) return 'Yesterday';
    if (diffDays < 7) return 'This week';
    if (diffDays < 30) return 'This month';
    return 'Older';
}

const GROUP_ORDER: DateGroup[] = [
    'Today',
    'Yesterday',
    'This week',
    'This month',
    'Older',
];

function formatTime(iso: string): string {
    const date = new Date(iso);
    const now = new Date();
    const isToday =
        date.getDate() === now.getDate() &&
        date.getMonth() === now.getMonth() &&
        date.getFullYear() === now.getFullYear();

    if (isToday) {
        return date.toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
        });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
}

// ---------------------------------------------------------------------------
// ConversationRow
// ---------------------------------------------------------------------------

interface ConversationRowProps {
    conversation: ConversationSummary;
    onClick: () => void;
}

function ConversationRow({ conversation, onClick }: ConversationRowProps) {
    return (
        <Box
            onClick={onClick}
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                px: 2,
                py: 1.25,
                cursor: 'pointer',
                borderRadius: '6px',
                mx: 1,
                transition: 'background 0.1s',
                '&:hover': {
                    bgcolor: 'action.hover',
                },
            }}
        >
            <ChatBubbleOutlineIcon
                sx={{ fontSize: 15, color: 'text.disabled', flexShrink: 0 }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    sx={{
                        fontSize: '13px',
                        fontWeight: 500,
                        color: 'text.primary',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                    }}
                >
                    {conversation.title || 'Untitled conversation'}
                </Typography>
            </Box>
            <Typography
                sx={{
                    fontSize: '11px',
                    color: 'text.disabled',
                    flexShrink: 0,
                    whiteSpace: 'nowrap',
                }}
            >
                {formatTime(conversation.updatedAt)}
            </Typography>
        </Box>
    );
}

// ---------------------------------------------------------------------------
// GroupLabel
// ---------------------------------------------------------------------------

function GroupLabel({ label }: { label: string }) {
    return (
        <Typography
            sx={{
                fontSize: '10.5px',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: 'text.disabled',
                px: 3,
                pt: 2,
                pb: 0.5,
            }}
        >
            {label}
        </Typography>
    );
}

// ---------------------------------------------------------------------------
// ConversationListView
// ---------------------------------------------------------------------------

export function ConversationListView() {
    const navigate = useNavigate();
    const { identity } = useGetIdentity();

    const [conversations, setConversations] = useState<ConversationSummary[]>(
        []
    );
    const [loading, setLoading] = useState(true);

    // -----------------------------------------------------------------------
    // Firestore subscription
    // -----------------------------------------------------------------------

    useEffect(() => {
        if (!identity?.id) return;

        const db = getFirestore();
        const q = query(
            collection(db, 'conversations'),
            where('userId', '==', identity.id),
            orderBy('updatedAt', 'desc')
        );

        const unsub = onSnapshot(
            q,
            snap => {
                const loaded: ConversationSummary[] = snap.docs.map(d => {
                    const data = d.data();
                    return {
                        id: data.id ?? d.id,
                        title: data.title ?? '',
                        updatedAt: toIso(data.updatedAt),
                        createdAt: toIso(data.createdAt),
                    };
                });
                setConversations(loaded);
                setLoading(false);
            },
            err => {
                console.error('ConversationListView: onSnapshot error', err);
                setLoading(false);
            }
        );

        return () => unsub();
    }, [identity?.id]);

    // -----------------------------------------------------------------------
    // Group conversations by date bucket
    // -----------------------------------------------------------------------

    const grouped = conversations.reduce<
        Record<DateGroup, ConversationSummary[]>
    >(
        (acc, conv) => {
            const group = getDateGroup(conv.updatedAt);
            if (!acc[group]) acc[group] = [];
            acc[group].push(conv);
            return acc;
        },
        {} as Record<DateGroup, ConversationSummary[]>
    );

    const activeGroups = GROUP_ORDER.filter(g => grouped[g]?.length > 0);

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------

    return (
        <Box
            sx={{
                position: 'fixed',
                // Between the bars: the phone bars below md, the desktop
                // header from md on (see FULL_SCREEN_INSETS).
                ...FULL_SCREEN_INSETS,
                display: 'flex',
                flexDirection: 'column',
                bgcolor: 'background.default',
                zIndex: t => t.zIndex.appBar - 1,
            }}
        >
            {/* Header */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 2,
                    py: 1.25,
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexShrink: 0,
                    bgcolor: 'background.paper',
                }}
            >
                <IconButton
                    size="small"
                    onClick={() => navigate('/markets')}
                    sx={{ color: 'text.secondary' }}
                >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            fontSize: '13px',
                            fontWeight: 600,
                            color: 'text.primary',
                        }}
                    >
                        Chats
                    </Typography>
                </Box>

                <Tooltip title="New chat">
                    <IconButton
                        size="small"
                        onClick={() => navigate('/chats/new')}
                        sx={{ color: 'text.secondary' }}
                    >
                        <AddIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>

            {/* Scrollable list */}
            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    py: 1,
                    ...hoverScrollbar,
                }}
            >
                {/* Loading skeletons */}
                {loading && (
                    <Box sx={{ px: 3, pt: 1 }}>
                        {Array.from({ length: 8 }).map((_, i) => (
                            <Box
                                key={i}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1.5,
                                    py: 1,
                                }}
                            >
                                <Skeleton
                                    variant="circular"
                                    width={15}
                                    height={15}
                                />
                                <Skeleton
                                    variant="text"
                                    width={`${60 + Math.random() * 25}%`}
                                    height={20}
                                />
                            </Box>
                        ))}
                    </Box>
                )}

                {/* Empty state */}
                {!loading && conversations.length === 0 && (
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            py: 10,
                            gap: 1.5,
                        }}
                    >
                        <ChatBubbleOutlineIcon
                            sx={{
                                fontSize: 32,
                                color: 'text.disabled',
                                opacity: 0.4,
                            }}
                        />
                        <Typography
                            sx={{ fontSize: '13px', color: 'text.disabled' }}
                        >
                            No conversations yet
                        </Typography>
                        <Typography
                            onClick={() => navigate('/chats/new')}
                            sx={{
                                fontSize: '12px',
                                color: 'primary.main',
                                cursor: 'pointer',
                                '&:hover': { textDecoration: 'underline' },
                            }}
                        >
                            Start a new chat
                        </Typography>
                    </Box>
                )}

                {/* Grouped conversations */}
                {!loading &&
                    activeGroups.map((group, groupIdx) => (
                        <Box key={group}>
                            {groupIdx > 0 && (
                                <Divider sx={{ my: 0.5, mx: 2 }} />
                            )}
                            <GroupLabel label={group} />
                            {grouped[group].map(conv => (
                                <ConversationRow
                                    key={conv.id}
                                    conversation={conv}
                                    onClick={() => navigate(`/chat/${conv.id}`)}
                                />
                            ))}
                        </Box>
                    ))}
            </Box>
        </Box>
    );
}
